import { useRef, type PointerEvent } from "react";

import type { DrawingPoint, FrameDrawing as FrameDrawingValue } from "../types";
import { cn } from "../lib/utils";

export const emptyDrawing = (): FrameDrawingValue => ({ strokes: [] });
export const hasDrawing = (drawing: FrameDrawingValue | null | undefined) => Boolean(drawing?.strokes.some((stroke) => stroke.points.length > 1));
const pathOf = (points: DrawingPoint[]) => points.map((point, index) => `${index ? "L" : "M"}${point.x} ${point.y}`).join(" ");

export function FrameDrawing({ value, editable = false, onChange, className }: {
  value?: FrameDrawingValue | null;
  editable?: boolean;
  onChange?: (value: FrameDrawingValue) => void;
  className?: string;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const active = useRef<{ pointerId: number; stroke: number } | null>(null);
  const drawing = value ?? emptyDrawing();
  const current = useRef(drawing);
  current.current = drawing;
  const change = (next: FrameDrawingValue) => {
    current.current = next;
    onChange?.(next);
  };
  const point = (event: PointerEvent<SVGSVGElement>): DrawingPoint => {
    const rect = svg.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1000, ((event.clientX - rect.left) / rect.width) * 1000)),
      y: Math.max(0, Math.min(1778, ((event.clientY - rect.top) / rect.height) * 1778)),
    };
  };
  return (
    <svg
      ref={svg}
      viewBox="0 0 1000 1778"
      preserveAspectRatio="none"
      className={cn("absolute inset-0 z-20 size-full touch-none", editable ? "cursor-crosshair" : "pointer-events-none", className)}
      onPointerDown={(event) => {
        if (!editable || !onChange || !event.isPrimary || active.current) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        active.current = { pointerId: event.pointerId, stroke: current.current.strokes.length };
        change({ strokes: [...current.current.strokes, { color: "#ef4444", width: 12, points: [point(event)] }] });
      }}
      onPointerMove={(event) => {
        if (!editable || !onChange || active.current?.pointerId !== event.pointerId) return;
        event.stopPropagation();
        const activeStroke = active.current.stroke;
        change({ strokes: current.current.strokes.map((stroke, index) => index === activeStroke ? { ...stroke, points: [...stroke.points, point(event)] } : stroke) });
      }}
      onPointerUp={(event) => {
        if (active.current?.pointerId !== event.pointerId) return;
        event.stopPropagation();
        active.current = null;
      }}
      onPointerCancel={(event) => {
        if (active.current?.pointerId === event.pointerId) active.current = null;
      }}
    >
      {drawing.strokes.map((stroke, index) => (
        <path key={index} d={pathOf(stroke.points)} fill="none" stroke={stroke.color} strokeWidth={stroke.width} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}
